import { Fragment, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Bell, Bot, Check, Copy, GitPullRequest, Inbox, Lightbulb, ListTodo, Plus, Sparkles } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, cn, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@vwt/ui';
import { API_BASE } from '../../lib/api-base.js';
import { useTranslation } from '../../lib/i18n.js';
import { PageHeader } from '../../components/common.js';

/** The remote MCP endpoint of this deployment (same origin in local development). */
function mcpServerUrl(): string {
  return `${API_BASE || window.location.origin}/mcp`;
}

/** Renders `code` and **bold** spans from catalog strings; everything else stays plain text. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((part, i) => {
        if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
          return (
            <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-fg">
              {part.slice(1, -1)}
            </code>
          );
        }
        if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
          return (
            <strong key={i} className="font-semibold text-fg">
              {part.slice(2, -2)}
            </strong>
          );
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('tutorial.copyFailed'));
    }
  };
  return (
    <Button variant="ghost" size="icon" className="size-7 shrink-0" aria-label={copied ? t('tutorial.copied') : t('tutorial.copy')} onClick={() => void copy()}>
      {copied ? <Check /> : <Copy />}
    </Button>
  );
}

/** Commands keep their lines; `wrap` is for prose meant to be pasted, such as project instructions. */
function CodeBlock({ code, wrap = false }: { code: string; wrap?: boolean }) {
  return (
    <div className="mt-2 flex items-start gap-2 rounded-[var(--radius)] border border-border bg-surface-2 py-1.5 pr-1.5 pl-3">
      <pre className={cn('min-w-0 flex-1 py-0.5 font-mono text-xs leading-relaxed text-fg', wrap ? 'break-words whitespace-pre-wrap' : 'overflow-x-auto')}>{code}</pre>
      <CopyButton value={code} />
    </div>
  );
}

type Step = { text: string; code?: string; wrap?: boolean };

function Steps({ steps }: { steps: Step[] }) {
  return (
    <ol className="flex flex-col gap-3">
      {steps.map((step, i) => (
        <li key={i} className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-3 text-sm text-fg-muted">
          <span className="flex size-6 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent-soft-fg" aria-hidden>
            {i + 1}
          </span>
          <div className="pt-0.5">
            <Rich text={step.text} />
            {step.code ? <CodeBlock code={step.code} wrap={step.wrap} /> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Note({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 rounded-[var(--radius)] border border-border bg-surface-2 p-3 text-sm text-fg-muted">
      <Lightbulb className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
      <div>
        {title ? <div className="mb-0.5 font-medium text-fg">{title}</div> : null}
        {children}
      </div>
    </div>
  );
}

function Section({ id, icon: Icon, title, intro, action, children }: { id: string; icon: typeof Bot; title: string; intro: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card id={id} className="scroll-mt-4">
      <CardHeader className="flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-accent" aria-hidden />
          <CardTitle>{title}</CardTitle>
        </div>
        {action}
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          <Rich text={intro} />
        </p>
        {children}
      </CardBody>
    </Card>
  );
}

const CLIENTS = ['claudeDesktop', 'claudeCode', 'chatgptDesktop', 'codex'] as const;
type Client = (typeof CLIENTS)[number];

function ConnectSection() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const url = mcpServerUrl();
  const requested = params.get('client');
  const client: Client = CLIENTS.includes(requested as Client) ? (requested as Client) : 'claudeDesktop';
  const k = (key: string) => t(`tutorial.connect.${key}`);

  const steps: Record<Client, Step[]> = {
    claudeDesktop: [
      { text: k('claudeDesktop.s1') },
      { text: k('claudeDesktop.s2'), code: url },
      { text: k('claudeDesktop.s3') },
      { text: k('claudeDesktop.s4') },
      { text: k('claudeDesktop.s5'), code: k('claudeDesktop.instructions'), wrap: true },
    ],
    claudeCode: [
      { text: k('claudeCode.s1'), code: `claude mcp add --transport http --scope user vammo ${url}` },
      { text: k('claudeCode.s2'), code: '/mcp' },
      { text: k('claudeCode.s3') },
    ],
    chatgptDesktop: [
      { text: k('chatgptDesktop.s1') },
      { text: k('chatgptDesktop.s2'), code: url },
      { text: k('chatgptDesktop.s3') },
      { text: k('chatgptDesktop.s4') },
    ],
    codex: [
      { text: k('codex.s1'), code: `[mcp_servers.vammo]\nurl = "${url}"` },
      { text: k('codex.s2'), code: 'codex mcp login vammo' },
      { text: k('codex.s3') },
    ],
  };

  return (
    <Section id="tutorial-connect" icon={Bot} title={k('title')} intro={k('intro')}>
      <div>
        <div className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">{k('serverUrl')}</div>
        <CodeBlock code={url} />
      </div>
      <Tabs value={client} onValueChange={(v) => setParams((p) => ({ ...Object.fromEntries(p), client: v }), { replace: true })}>
        <TabsList className="flex-wrap">
          {CLIENTS.map((c) => (
            <TabsTrigger key={c} value={c}>
              {k(`${c}.name`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {CLIENTS.map((c) => (
          <TabsContent key={c} value={c} className="flex flex-col gap-4 pt-4">
            <p className="text-sm text-fg-subtle">
              <Rich text={k(`${c}.summary`)} />
            </p>
            <Steps steps={steps[c]} />
            {c === 'chatgptDesktop' ? (
              <Note>
                <Rich text={k('chatgptDesktop.note')} />
              </Note>
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
      <Note title={k('verifyTitle')}>
        <Rich text={k('verify')} />
      </Note>
      <p className="text-xs text-fg-subtle">
        <Rich text={k('adminNote')} />
      </p>
    </Section>
  );
}

function RequestSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const k = (key: string) => t(`tutorial.request.${key}`);
  const ways = ['web', 'assistant', 'slack'] as const;
  return (
    <Section
      id="tutorial-request"
      icon={Inbox}
      title={k('title')}
      intro={k('intro')}
      action={
        <Button size="sm" variant="secondary" onClick={() => navigate('/requests/new')}>
          <Plus /> {t('nav.newRequest')}
        </Button>
      }
    >
      <div className="grid gap-3 md:grid-cols-3">
        {ways.map((way) => (
          <div key={way} className="rounded-[var(--radius)] border border-border p-3">
            <div className="mb-1 text-sm font-medium text-fg">{k(`${way}.title`)}</div>
            <p className="text-sm text-fg-muted">
              <Rich text={k(`${way}.body`)} />
            </p>
          </div>
        ))}
      </div>
      <div>
        <div className="mb-2 text-sm font-medium text-fg">{k('tipsTitle')}</div>
        <ul className="list-disc space-y-1 pl-5 text-sm text-fg-muted">
          {(['t1', 't2', 't3'] as const).map((tip) => (
            <li key={tip}>
              <Rich text={k(tip)} />
            </li>
          ))}
        </ul>
      </div>
      <Note>
        <Rich text={k('after')} />
      </Note>
    </Section>
  );
}

function TeamWorkSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const k = (key: string) => t(`tutorial.team.${key}`);
  return (
    <Section
      id="tutorial-team"
      icon={ListTodo}
      title={k('title')}
      intro={k('intro')}
      action={
        <Button size="sm" variant="secondary" onClick={() => navigate('/work?create=1')}>
          <Plus /> {t('nav.newWork')}
        </Button>
      }
    >
      <Steps steps={(['s1', 's2', 's3', 's4', 's5', 's6'] as const).map((s) => ({ text: k(s) }))} />
      <Note title={k('assistantTitle')}>
        <Rich text={k('assistant')} />
      </Note>
    </Section>
  );
}

function NotificationsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const k = (key: string) => t(`tutorial.notifications.${key}`);
  const channels = ['browser', 'email', 'slack'] as const;
  return (
    <Section
      id="tutorial-notifications"
      icon={Bell}
      title={k('title')}
      intro={k('intro')}
      action={
        <Button size="sm" variant="secondary" onClick={() => navigate('/settings?tab=notifications')}>
          <Bell /> {k('open')}
        </Button>
      }
    >
      <Steps steps={(['s1', 's2', 's3', 's4'] as const).map((s) => ({ text: k(s) }))} />
      <div className="grid gap-3 md:grid-cols-3">
        {channels.map((channel) => (
          <div key={channel} className="rounded-[var(--radius)] border border-border p-3">
            <div className="mb-1 text-sm font-medium text-fg">{k(`${channel}.title`)}</div>
            <p className="text-sm text-fg-muted">
              <Rich text={k(`${channel}.body`)} />
            </p>
          </div>
        ))}
      </div>
      <Note title={k('troubleTitle')}>
        <ul className="list-disc space-y-1 pl-4">
          {(['blocked', 'iphone', 'missing'] as const).map((tip) => (
            <li key={tip}>
              <Rich text={k(tip)} />
            </li>
          ))}
        </ul>
      </Note>
    </Section>
  );
}

function TrackingSection() {
  const { t } = useTranslation();
  const k = (key: string) => t(`tutorial.tracking.${key}`);
  const flow = ['f1', 'f2', 'f3', 'f4'] as const;
  return (
    <Section id="tutorial-tracking" icon={GitPullRequest} title={k('title')} intro={k('intro')}>
      <ol className="grid gap-3 md:grid-cols-2">
        {flow.map((f, i) => (
          <li key={f} className="rounded-[var(--radius)] border border-border p-3">
            <div className="mb-1 flex items-center gap-2 text-sm font-medium text-fg">
              <span className="flex size-5 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent-soft-fg" aria-hidden>
                {i + 1}
              </span>
              {k(`${f}.title`)}
            </div>
            <p className="text-sm text-fg-muted">
              <Rich text={k(`${f}.body`)} />
            </p>
          </li>
        ))}
      </ol>
      <Note title={k('keyTipTitle')}>
        <Rich text={k('keyTip')} />
      </Note>
      <Note title={k('ambiguousTitle')}>
        <Rich text={k('ambiguous')} />
      </Note>
    </Section>
  );
}

/** Onboarding guide: connect an AI assistant, file requests, create team work, notifications and how code work is tracked. */
export function TutorialPage() {
  const { t } = useTranslation();
  const toc = [
    { id: 'tutorial-connect', label: t('tutorial.connect.title') },
    { id: 'tutorial-request', label: t('tutorial.request.title') },
    { id: 'tutorial-team', label: t('tutorial.team.title') },
    { id: 'tutorial-notifications', label: t('tutorial.notifications.title') },
    { id: 'tutorial-tracking', label: t('tutorial.tracking.title') },
  ];
  return (
    <div className="flex min-h-full flex-col">
      <PageHeader title={t('tutorial.title')} subtitle={t('tutorial.subtitle')} />
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 md:p-6">
        <nav aria-label={t('tutorial.toc')} className="flex flex-wrap gap-2">
          {toc.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => document.getElementById(item.id)?.scrollIntoView({ block: 'start' })}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-fg-muted hover:border-border-strong hover:text-fg"
            >
              <span className="text-fg-subtle">{i + 1}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <ConnectSection />
        <RequestSection />
        <TeamWorkSection />
        <NotificationsSection />
        <TrackingSection />
        <p className="flex items-center gap-2 pb-4 text-xs text-fg-subtle">
          <Sparkles className="size-3.5" aria-hidden />
          {t('tutorial.footer')}
        </p>
      </div>
    </div>
  );
}
